import { zodResolver } from '@hookform/resolvers/zod'
import { Cancel as CancelIcon, Save as SaveIcon } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { type TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { SelectElement, TextFieldElement } from 'react-hook-form-mui'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'

import iconProprity from '~/assets/iconPriority.png'
import BottomSpacer from '~/components/BottomSpacer'
import TitleBar from '~/components/TitleBar'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { updateDraft } from '~/redux/slices/currentDraft'
import { COLORS } from '~/theme'
import { constructEstimatedMonthsOptions } from '~/utils/form-utils'
import { isTimerNum, remakeTimers, screenTimer } from '~/utils/survey-utils'
import {
  StoplightColorValue,
  type LifemapQuestionType,
  type SurveyLifemapQuestion,
} from '~/utils/types/surveys'

type PriorityTranslations = Extract<
  LifemapQuestionType,
  'PRIORITY_REASON' | 'PRIORITY_ACTION' | 'PRIORITY_ESTIMATED_DATE'
>

function getQuestionTitles(
  t: TFunction,
  questions: SurveyLifemapQuestion[],
  key: PriorityTranslations,
) {
  const translations: { [key in PriorityTranslations]: string } = {
    PRIORITY_REASON: t('views.lifemap.whyDontYouHaveIt'),
    PRIORITY_ACTION: t('views.lifemap.whatWillYouDoToGetIt'),
    PRIORITY_ESTIMATED_DATE: t('views.lifemap.howManyMonthsWillItTake'),
  }

  const label =
    questions.find(q => q.questionType === key)?.questionText ??
    translations[key]

  return label
}

enum ImageStatus {
  Loading = 'loading',
  Loaded = 'loaded',
}

export default function Priority() {
  const params = useParams()
  const { indicator } = params
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { t } = useTranslation()
  const [imageStatus, setImageStatus] = useState<ImageStatus>(
    ImageStatus.Loading,
  )

  const currentDraft = useAppSelector(state => {
    if (!state.currentDraft) throw new Error('No currentDraft')
    return state.currentDraft
  })
  const currentSurvey = useAppSelector(state => {
    if (!state.currentSurvey) throw new Error('No currentSurvey')
    return state.currentSurvey
  })

  const { lifemapQuestions } = currentSurvey
  const monthsOptions = constructEstimatedMonthsOptions(t)

  const priority = currentDraft.priorities.find(p => p.indicator === indicator)
  const question = currentSurvey.surveyStoplightQuestions.find(
    q => q.codeName === indicator,
  )

  const validationSchema = z.object({
    priorityReason: z
      .string()
      .max(300, t('validation.maxLength', { max: 300 }))
      .optional(),
    priorityAction: z
      .string()
      .max(300, t('validation.maxLength', { max: 300 }))
      .optional(),
    estimatedDate: z.union([z.number(), z.null()]).transform((val, ctx) => {
      if (!val) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('validation.fieldIsRequired'),
          fatal: true,
        })
        return z.NEVER
      }

      const parsedVal = parseInt(val.toString())
      if (isNaN(parsedVal)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('validation.fieldIsRequired'),
          fatal: true,
        })
        return z.NEVER
      }

      return parsedVal
    }),
  })

  type FormValues = z.infer<typeof validationSchema>

  const form = useForm<FormValues>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    defaultValues: {
      priorityReason: priority?.reason ?? '',
      priorityAction: priority?.action ?? '',
      estimatedDate: priority?.estimatedDate,
    },
  })

  const stoplightAnswer = currentDraft.indicatorSurveyDataList.find(
    answers => answers.key === question?.codeName,
  )

  const stoplightColor = question?.stoplightColors.find(
    e => e.value === stoplightAnswer?.value,
  )

  const color = useMemo(() => {
    if (!stoplightColor) return null
    const colorMap = {
      [StoplightColorValue.RED]: COLORS.RED,
      [StoplightColorValue.YELLOW]: COLORS.YELLOW,
      [StoplightColorValue.GREEN]: COLORS.GREEN,
      [StoplightColorValue.SKIPPED]: COLORS.SKIPPED_GREY,
      [StoplightColorValue.DOES_NOT_APPLY]: COLORS.DOES_NOT_APPLY_GREY,
    }

    const textColor =
      stoplightColor.value === StoplightColorValue.YELLOW ? 'black' : 'white'

    return {
      textColor,
      bgColor: colorMap[stoplightColor.value],
    }
  }, [stoplightColor])

  function savePriority(values: FormValues) {
    const { priorityReason, priorityAction, estimatedDate } = values

    const newPriority = {
      reason: priorityReason ?? '',
      action: priorityAction ?? '',
      estimatedDate,
      indicator: question?.codeName ?? '',
    }

    const item = currentDraft.priorities.filter(
      priority => priority.indicator === question?.codeName,
    )[0]

    // If the item does not exist, we create it
    if (!item) {
      dispatch(
        updateDraft({
          ...currentDraft,
          priorities: [...currentDraft.priorities, newPriority],
        }),
      )
      navigate(-1)
      return
    }

    // else, we update it
    const index: number = currentDraft.priorities.indexOf(item)
    dispatch(
      updateDraft({
        ...currentDraft,
        priorities: [
          ...currentDraft.priorities.slice(0, index),
          newPriority,
          ...currentDraft.priorities.slice(index + 1),
        ],
      }),
    )

    navigate(-1)
  }

  useEffect(() => {
    let updatedDraft = { ...currentDraft }

    if (isTimerNum(updatedDraft)) {
      const remakedTimers = remakeTimers(updatedDraft)
      updatedDraft = { ...updatedDraft, ...remakedTimers }
    }

    const screenTimes = screenTimer(updatedDraft, 'Overview')
    dispatch(updateDraft({ ...updatedDraft, ...screenTimes }))
  }, [dispatch])

  if (!question) return <Navigate to={'/lifemap/overview'} />

  return (
    <Container>
      <TitleBar
        progressBar={false}
        title={question.questionText}
        extraTitleText={question.dimension}
      />
      <>
        <Stack alignItems="center" py={2}>
          <Card
            variant="outlined"
            sx={{ maxWidth: 480, bgcolor: color?.bgColor, p: 2 }}
          >
            <Stack alignItems="center" justifyContent="center">
              {imageStatus === ImageStatus.Loading && (
                <>
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{ width: { xs: '100%', md: 240 }, aspectRatio: 1 }}
                  >
                    <CircularProgress sx={{ color: 'white' }} />
                  </Stack>

                  <CardMedia
                    component="img"
                    src={stoplightColor?.urlImage}
                    alt={question.shortName}
                    sx={{ display: 'none' }}
                    onLoad={() => {
                      setImageStatus(ImageStatus.Loaded)
                    }}
                  />
                </>
              )}

              {imageStatus === ImageStatus.Loaded && (
                <CardMedia
                  component="img"
                  src={stoplightColor?.urlImage}
                  alt={question.shortName}
                  sx={{
                    aspectRatio: 1,
                    borderRadius: 2,
                    width: { xs: '100%', md: 240 },
                  }}
                />
              )}
            </Stack>

            <CardContent>
              <Divider />
              <Typography
                variant="body1"
                textAlign="center"
                sx={{ color: color?.textColor }}
              >
                {stoplightColor?.description}
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        <Stack
          gap={2}
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <CardMedia
            alt="icon"
            component="img"
            src={iconProprity}
            sx={{ height: 55, objectFit: 'contain' }}
          />

          <Typography variant="h5" align="center">
            {t('views.lifemap.makeThisAPriority')}
          </Typography>
        </Stack>

        <Container maxWidth="md">
          <Stack
            pt={2}
            gap={2}
            component="form"
            onSubmit={form.handleSubmit(savePriority)}
          >
            <TextFieldElement
              fullWidth
              autoFocus
              control={form.control}
              autoComplete="off"
              name="priorityReason"
              label={getQuestionTitles(t, lifemapQuestions, 'PRIORITY_REASON')}
            />

            <TextFieldElement
              fullWidth
              control={form.control}
              autoComplete="off"
              name="priorityAction"
              label={getQuestionTitles(t, lifemapQuestions, 'PRIORITY_ACTION')}
            />

            <SelectElement
              name="estimatedDate"
              control={form.control}
              label={getQuestionTitles(
                t,
                lifemapQuestions,
                'PRIORITY_ESTIMATED_DATE',
              )}
              options={monthsOptions.map(month => ({
                id: month.value,
                label: month.label,
              }))}
            />

            <Stack
              alignItems="center"
              direction="row"
              justifyContent="center"
              gap={2}
            >
              <Button
                variant="outlined"
                endIcon={<CancelIcon />}
                disabled={form.formState.isSubmitting}
                onClick={() => {
                  navigate('/lifemap/overview')
                }}
              >
                {t('general.cancel')}
              </Button>

              <Button
                type="submit"
                color="primary"
                variant="contained"
                endIcon={<SaveIcon />}
                loading={form.formState.isSubmitting}
              >
                {t('general.save')}
              </Button>
            </Stack>
          </Stack>
        </Container>
        <BottomSpacer />
      </>
    </Container>
  )
}
